import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Estate_UpdateEstateInput, EstateFiduciaryRole } from 'queries';

import FormDateField from 'common/form-date-field';
import FormInputErrorMessage from 'common/form-input-error-message';
import FormPhoneField from 'common/form-phone-field';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';
import PortalUsersAutocomplete, {
  UserAutoCompleteOption,
} from 'pages/common/portal/portal-users-autocomplete';

const useStyles = makeStyles(() =>
  createStyles({
    h5: {
      paddingTop: '5px',
    },
    formSection: {
      paddingBottom: '10px',
    },
    inputField: {
      width: '100%',
    },
    inputDropdown: {
      width: '100%',
      backgroundColor: 'white',
      height: '4.5vh',
    },
    inputGrid: {
      paddingRight: '20px',
    },
    inputError: {
      backgroundColor: '#FFF1F1',
      color: 'red',
    },
  }),
);

type Props = {
  portalId: string;
  isEditable: boolean;
};

const EstateDetailTruadminForm = ({ portalId, isEditable }: Props) => {
  const classes = useStyles();

  const {
    formState: { errors },
    control,
  } = useFormContext<Estate_UpdateEstateInput>();

  return (
    <>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Decedent
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentFirstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  label="First Name"
                  disabled={!isEditable}
                  errorMessage={errors.estateTruadminFields?.decedentFirstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentMiddleName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  label="Middle Name or Initial"
                  helperText="*Optional. Please leave blank if no middle name."
                  disabled={!isEditable}
                  errorMessage={errors.estateTruadminFields?.decedentMiddleName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruadminFields.decedentLastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  label="Last Name"
                  disabled={!isEditable}
                  errorMessage={errors.estateTruadminFields?.decedentLastName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentSsn"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSsnField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruadminFields?.decedentSsn?.message}
                  label="SSN"
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentDateOfBirth"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <FormDateField
                  {...{ value, onChange, name }}
                  label="Date of Birth"
                  errorMessage={
                    (errors.estateTruadminFields?.decedentDateOfBirth?.message as string) ||
                    undefined
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentDateOfDeath"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <FormDateField
                  {...{ value, onChange, name }}
                  label="Date of Death"
                  errorMessage={
                    (errors.estateTruadminFields?.decedentDateOfDeath?.message as string) ||
                    undefined
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container style={{ display: 'inline' }}>
          <Grid style={{ display: 'inline' }} className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentLastAddressStreet"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="Last Address Street"
                  errorMessage={errors.estateTruadminFields?.decedentLastAddressStreet?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.decedentLastAddressCity"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="Last Address City"
                  errorMessage={errors.estateTruadminFields?.decedentLastAddressCity?.message}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={3} sm={2}>
            <Controller
              name="estateTruadminFields.decedentLastAddressState"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="State"
                  errorMessage={errors.estateTruadminFields?.decedentLastAddressState?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={9} sm={4}>
            <Controller
              name="estateTruadminFields.decedentLastAddressZip"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Last Address Zip"
                  inputProps={{ maxLength: 10 }}
                  errorMessage={errors.estateTruadminFields?.decedentLastAddressZip?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Estate Representative
        </Typography>
        <Typography variant="caption">
          This person should have the authority to act on behalf of the decedent's estate or trust.
          For instance, an Executor, Personal Representative, Administrator, Trustee, or next of kin
          (i.e., a surviving spouse).
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientFirstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="First Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientFirstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientMiddleName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  label="Middle Name or Initial"
                  helperText="*Optional. Please leave blank if no middle name."
                  disabled={!isEditable}
                  errorMessage={errors.estateTruadminFields?.clientMiddleName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruadminFields.clientLastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Last Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientLastName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.corporatePersonalRepresentativeName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  label="Corporate Personal Representative Name"
                  disabled={!isEditable}
                  errorMessage={
                    errors.estateTruadminFields?.corporatePersonalRepresentativeName?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.isTheClientTheSurvivingSpouse"
              control={control}
              render={({ field: { ref, onChange, value, ...field } }) => (
                <FormSelectField
                  {...field}
                  onChange={(e) => onChange(e.target.value === 'yes')}
                  value={value ? 'yes' : 'no'}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                  label="Is the client the surviving spouse"
                  errorMessage={errors.estateTruadminFields?.isTheClientTheSurvivingSpouse?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientEmailAddress"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Email Address"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientEmailAddress?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientPhoneNumber"
              control={control}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <FormPhoneField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.estateTruadminFields?.clientPhoneNumber?.message}
                  label="Phone Number"
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container style={{ display: 'inline' }}>
          <Grid style={{ display: 'inline' }} className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientAddressStreet"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Street"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientAddressStreet?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientAddressCity"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address City"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientAddressCity?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={3} sm={2}>
            <Controller
              name="estateTruadminFields.clientAddressState"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="State"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruadminFields?.clientAddressState?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
          <Grid item xs={9} sm={4}>
            <Controller
              name="estateTruadminFields.clientAddressZip"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Zip"
                  inputProps={{ maxLength: 10 }}
                  errorMessage={errors.estateTruadminFields?.clientAddressZip?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruadminFields.clientFiduciaryRole"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSelectField
                  {...field}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Administrator - Appointed by court',
                      value: EstateFiduciaryRole.AdministratorCourtAppointed,
                    },
                    {
                      label: 'Administrator - Court appointment pending',
                      value: EstateFiduciaryRole.AdministratorNotAppointed,
                    },
                    {
                      label: 'Corporate Executor - Appointed',
                      value: EstateFiduciaryRole.CorporateExecutorAppointed,
                    },
                    {
                      label: 'Corporate Trustee - Appointed',
                      value: EstateFiduciaryRole.CorporateTrusteeAppointed,
                    },
                    {
                      label: 'Curator',
                      value: EstateFiduciaryRole.Curator,
                    },
                    {
                      label: 'Executor - Appointed by court',
                      value: EstateFiduciaryRole.ExecutorCourtAppointed,
                    },
                    {
                      label: 'Executor - Named in Will, not yet appointed by court',
                      value: EstateFiduciaryRole.ExecutorNotAppointed,
                    },
                    {
                      label: 'Next of Kin',
                      value: EstateFiduciaryRole.NextOfKin,
                    },
                    {
                      label: 'Personal Representative',
                      value: EstateFiduciaryRole.PersonalRepresentative,
                    },
                    {
                      label: 'Surviving Spouse',
                      value: EstateFiduciaryRole.SurvivingSpouse,
                    },
                    {
                      label: 'Trustee',
                      value: EstateFiduciaryRole.Trustee,
                    },
                    {
                      label: 'Unspecified',
                      value: EstateFiduciaryRole.Unspecified,
                    },
                  ]}
                  label="Role with respect to estate"
                  errorMessage={errors.estateTruadminFields?.clientFiduciaryRole?.message}
                />
              )}
            />
            {errors.estateTruadminFields?.clientFiduciaryRole?.message && (
              <FormInputErrorMessage
                error={errors.estateTruadminFields?.clientFiduciaryRole?.message}
              />
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Add a Point Person
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={12}>
            <Controller
              name="assignedUserId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PortalUsersAutocomplete
                  elementId="assignedUserId"
                  portalId={portalId}
                  selectedUserId={value || ''}
                  handleUserChange={(
                    _elementId: string,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    _e: any,
                    option: UserAutoCompleteOption | null,
                  ) => onChange(option?.id)}
                />
              )}
            />
            {errors.assignedUserId?.message && (
              <FormInputErrorMessage error={errors.assignedUserId?.message} />
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default EstateDetailTruadminForm;
